import React, { useEffect, useMemo, useRef, useState } from "react";
import AutocompleteInput from "./Autocomplete";
import "./ZleceniaStyle/KosztySprzedazTabela.css";
import { hasPermission } from "../../auth/permissionUtils";

function round2(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return "";
  return Math.round((x + Number.EPSILON) * 100) / 100;
}
function toNum(v) {
  if (v === "" || v == null) return NaN;
  const n = Number(String(v).replace(",", "."));
  return Number.isFinite(n) ? n : NaN;
}

function qtyStatus(v) {
  if (v === "" || v == null) return "empty";
  const n = toNum(v);
  if (!Number.isFinite(n)) return "empty";
  if (n <= 0) return "nonpositive";
  return null;
}

function blockNonNegativeKeys(e) {
  const bad = ["e", "E", "+", "-"];
  if (bad.includes(e.key)) e.preventDefault();
}

function blockPercentKeys(e) {
  const k = e.key;
  if (k === "e" || k === "E" || k === "+") {
    e.preventDefault();
    return;
  }
  if (k === "-") {
    const el = e.currentTarget;
    const hasMinus = el.value.includes("-");
    const caret = el.selectionStart ?? 0;
    if (hasMinus || caret !== 0) e.preventDefault();
  }
}

export default function KosztySprzedazTabela({
  towary = [],
  slowniki = { kategorie: [], jednostki: [], stawkiVat: [] },
  onChange,
  initialRows = [],
  uzytkownik,
}) {
  const canSeeCostsMargin = !hasPermission(uzytkownik, 'hide_costs_margin');
  const canSeeSalePrices = !hasPermission(uzytkownik, 'hide_sale_prices');
  const nextId = useRef(1);
  const [rows, setRows] = useState([]);

  const editingAnyId = useMemo(() => {
    const r = rows.find(r => r.isEditing || r.isEditingSales);
    return r ? r.id : null;
  }, [rows]);

  const otherRowEditing = (rowId) => editingAnyId != null && editingAnyId !== rowId;

  useEffect(() => {
    if (initialRows?.length) {
      const mapped = initialRows.map((r) =>
        computeDerived({ ...r, isEditingSales: false, cena_manual: false })
      );
      setRows(mapped);
      const maxUiId = initialRows.reduce((m, r) => Math.max(m, r.id || 0), 0) || 0;
      nextId.current = maxUiId + 1;
    }
  }, [initialRows]);

  useEffect(() => {
    if (typeof onChange === "function") onChange(rows);
  }, [rows, onChange]);

  const makeEmptyRow = () => computeDerived({
    id: nextId.current++,
    isEditing: true,
    isEditingSales: false,
    _showQtyError: false,
    _showNameError: false,
    id_koszt: null,
    id_towar: null,
    kategoria_id: null,
    jednostka_id: null,
    vat_id: null,
    nazwa_at_sale: "",
    kategoria_nazwa_at_sale: "",
    jednostka_symbol_at_sale: "",
    ilosc: "",
    koszt_jednostkowy_netto_at_sale: "",
    marza_at_sale: "",
    vat_nazwa_at_sale: "",
    vat_proc_at_sale: "",
    in_sprzedaz: true,
    cena_netto_at_sale: "",
    cena_manual: false,
  });

  function setRowProp(id, key, value) {
    setRows((prev) => prev.map((r) => r.id === id ? computeDerived({ ...r, [key]: value }) : r));
  }

  function computeDerived(row) {
    const ilosc = toNum(row.ilosc);
    const koszt = toNum(row.koszt_jednostkowy_netto_at_sale);
    const vat = toNum(row.vat_proc_at_sale);

    const kwota_vat_koszt = Number.isFinite(koszt) && Number.isFinite(vat) ? round2(koszt * (vat / 100)) : "";
    const razem_netto_koszt = Number.isFinite(koszt) && Number.isFinite(ilosc) ? round2(koszt * ilosc) : "";
    const razem_vat_koszt = kwota_vat_koszt !== "" && Number.isFinite(ilosc) ? round2(kwota_vat_koszt * ilosc) : "";
    const razem_brutto_koszt = razem_netto_koszt !== "" && Number.isFinite(vat) ? round2(razem_netto_koszt * (1 + vat / 100)) : "";

    let cenaNetto = toNum(row.cena_netto_at_sale);
    const marza = toNum(row.marza_at_sale);
    if (!row.cena_manual) {
      cenaNetto = (Number.isFinite(koszt) && Number.isFinite(marza)) ? round2(koszt * (1 + marza / 100)) : "";
    }

    const cenaBruttoUnit = Number.isFinite(cenaNetto) && Number.isFinite(vat) ? round2(cenaNetto * (1 + vat / 100)) : "";
    const wartoscNetto = Number.isFinite(cenaNetto) && Number.isFinite(ilosc) ? round2(cenaNetto * ilosc) : "";
    const kwotaVatSprz = wartoscNetto !== "" && Number.isFinite(vat) ? round2(cenaNetto * (vat / 100)) : "";
    const wartoscBrutto = wartoscNetto !== "" && kwotaVatSprz !== "" ? round2(wartoscNetto + (kwotaVatSprz * ilosc)) : "";

    return {
      ...row,
      kwota_vat: kwota_vat_koszt,
      razem_vat: razem_vat_koszt,
      razem_netto: razem_netto_koszt,
      razem_brutto: razem_brutto_koszt,
      cena_netto_at_sale: Number.isFinite(cenaNetto) ? cenaNetto : (row.cena_netto_at_sale === "" ? "" : row.cena_netto_at_sale),
      sprz_cena_brutto_unit: cenaBruttoUnit,
      sprz_kwota_vat: kwotaVatSprz,
      sprz_razem_netto: wartoscNetto,
      sprz_razem_brutto: wartoscBrutto,
    };
  }

  function handleAddRow() {
    if (editingAnyId != null) {
      alert('Najpierw zapisz albo usuń edytowany wiersz.');
      return;
    }
    setRows(prev => [makeEmptyRow(), ...prev]);
  }

  function handleRemoveRow(id) {
    if (window.confirm("Czy na pewno usunąć tę pozycję?")) {
        setRows((prev) => prev.filter((r) => r.id !== id));
    }
  }

  function handleNameChange(rowId, val) {
    setRows(prev => prev.map(r => r.id === rowId ? computeDerived({ ...r, nazwa_at_sale: val, id_towar: null, _showNameError: false }) : r));
  }

  function handleSaveRow(id) {
    setRows(prev => prev.map(r => {
      if (r.id !== id) return r;
      const qtyErr = qtyStatus(r.ilosc);
      const nameErr = !r.id_towar;
      if (qtyErr || nameErr) {
        return { ...r, isEditing: true, _showQtyError: !!qtyErr, _showNameError: !!nameErr };
      }
      return { ...r, isEditing: false, _showQtyError: false, _showNameError: false };
    }));
  }

  function handleEditRow(rowId) {
    if (otherRowEditing(rowId)) {
      alert('Najpierw zapisz/usuń aktualnie edytowany wiersz.');
      return;
    }
    setRows(prev => prev.map(r => r.id === rowId ? { ...r, isEditing: true, isEditingSales: false, _showQtyError: false, _showNameError: false } : r));
  }

  function handleSalesEditRow(rowId) {
    if (otherRowEditing(rowId)) {
      alert('Najpierw zapisz/usuń aktualnie edytowany wiersz.');
      return;
    }
    setRows(prev => prev.map(r => r.id === rowId ? { ...r, isEditingSales: true, isEditing: false } : r));
  }

  function handleSalesSaveRow(id) {
    setRowProp(id, "isEditingSales", false);
  }

  function handleTowarSelect(rowId, t) {
    const kat = slowniki.kategorie?.find((k) => k.id === t.kategoria_id);
    const jm = slowniki.jednostki?.find((j) => j.id === t.jednostka_id);
    const vatEntry = slowniki.stawkiVat?.find((v) => String(v.proc) === String(t.vat_proc) || v.id === t.vat_id || v.nazwa === t.vat_nazwa);

    const payload = {
      id_towar: t.id,
      nazwa_at_sale: t.nazwa || "",
      kategoria_id: t.kategoria_id ?? null,
      jednostka_id: t.jednostka_id ?? null,
      koszt_jednostkowy_netto_at_sale: t.koszt_netto != null ? Number(t.koszt_netto) : "",
      marza_at_sale: t.marza_proc != null ? Number(t.marza_proc) : "",
      vat_proc_at_sale: t.vat_proc != null ? Number(t.vat_proc) : "",
      kategoria_nazwa_at_sale: kat?.nazwa || "",
      jednostka_symbol_at_sale: jm?.symbol || "",
      vat_nazwa_at_sale: vatEntry?.nazwa || "",
      vat_id: vatEntry?.id ?? null,
      cena_netto_at_sale: (t.koszt_netto != null && t.marza_proc != null) ? round2(Number(t.koszt_netto) * (1 + Number(t.marza_proc) / 100)) : "",
      isEditingSales: false,
      cena_manual: false,
    };

    setRows(prev => prev.map(r => r.id === rowId ? computeDerived({ ...r, ...payload, _showNameError: false }) : r));
  }

  function handleQtyChange(rowId, value) {
    setRows(prev => prev.map(r => r.id === rowId ? computeDerived({ ...r, ilosc: value, _showQtyError: qtyStatus(value) ? r._showQtyError : false }) : r));
  }

  function handleVatChange(rowId, vatValue) {
    const entry = slowniki.stawkiVat?.find((v) => String(v.proc) === String(vatValue) || v.id === vatValue) || null;
    setRows((prev) => prev.map((r) => r.id === rowId ? computeDerived({ ...r, vat_proc_at_sale: entry ? Number(entry.proc) : "", vat_id: entry?.id ?? null, vat_nazwa_at_sale: entry?.nazwa || "" }) : r));
  }

  function handleSalesPriceChange(rowId, val) {
    const cena = toNum(val);
    setRows((prev) => prev.map((r) => {
      if (r.id !== rowId) return r;
      const koszt = toNum(r.koszt_jednostkowy_netto_at_sale);
      let newMarza = r.marza_at_sale;
      if (Number.isFinite(cena) && Number.isFinite(koszt) && koszt !== 0) {
        newMarza = round2((cena / koszt - 1) * 100);
      } else if (val === "" || !Number.isFinite(cena)) {
        return computeDerived({ ...r, cena_netto_at_sale: "", cena_manual: false });
      }
      return computeDerived({ ...r, cena_netto_at_sale: Number.isFinite(cena) ? cena : val, marza_at_sale: newMarza, cena_manual: true });
    }));
  }

  const sumsKoszt = useMemo(() => rows.reduce((acc, r) => {
    acc.netto += Number(r.razem_netto || 0);
    acc.brutto += Number(r.razem_brutto || 0);
    return acc;
  }, { netto: 0, brutto: 0 }), [rows]);

  const sumsSprzedaz = useMemo(() => rows.filter((r) => !!r.in_sprzedaz).reduce((acc, r) => {
    acc.netto += Number(r.sprz_razem_netto || 0);
    acc.brutto += Number(r.sprz_razem_brutto || 0);
    return acc;
  }, { netto: 0, brutto: 0 }), [rows]);

  return (
    <div className="kst-container">
      {/* ======== KOSZTY ======== */}
      <div className="kst-section">
        <div className="kst-section-header">
          <h4>Koszty (zakup)</h4>
          <button
            className={`add-cost-button ${editingAnyId != null ? 'btn-disabled' : ''}`}
            onClick={handleAddRow}
            type="button"
            disabled={editingAnyId != null}
          >
            + Dodaj koszt
          </button>
        </div>

        {/* WERSJA DESKTOP */}
        <div className="desktop-view">
          <div className="kst-card">
            <table className="costs-table">
              <thead>
                <tr>
                  <th>L.P.</th>
                  <th>NAZWA</th>
                  <th>KATEGORIA</th>
                  <th>ILOŚĆ</th>
                  <th>J.M.</th>
                  {canSeeCostsMargin
                  ?<th>KOSZT NETTO</th>
                  :<></>
                  }
                  {canSeeCostsMargin
                  ?<th>MARŻA %</th>
                  :<></>
                  }
                  <th>STAWKA</th>
                  {canSeeCostsMargin
                  ?<th>RAZEM NETTO</th>
                  :<></>
                  }
                  {canSeeCostsMargin
                  ?<th>RAZEM BRUTTO</th>
                  :<></>
                  }
                  <th>AKCJE</th>
                  <th>UJĘTE?</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr key={row.id}>
                    <td>{idx + 1}</td>
                    <td>
                      {row.isEditing ? (
                        <AutocompleteInput
                          value={row.nazwa_at_sale}
                          towary={towary}
                          onChange={(val) => handleNameChange(row.id, val)}
                          onSelect={(item) => handleTowarSelect(row.id, item)}
                        />
                      ) : row.nazwa_at_sale}
                    </td>
                    <td>{row.kategoria_nazwa_at_sale}</td>
                    <td>
                      {row.isEditing ? (
                        <input className="table-input" type="number" value={row.ilosc} onChange={(e) => handleQtyChange(row.id, e.target.value)} />
                      ) : row.ilosc}
                    </td>
                    <td>{row.jednostka_symbol_at_sale}</td>
                    {canSeeCostsMargin
                    ?<td>
                      {row.isEditing ? (
                        <input className="table-input" type="number" value={row.koszt_jednostkowy_netto_at_sale} onChange={(e) => setRowProp(row.id, "koszt_jednostkowy_netto_at_sale", e.target.value)} />
                      ) : row.koszt_jednostkowy_netto_at_sale}
                    </td>
                    :<></>
                    }

                    {canSeeCostsMargin
                    ?<td>
                      {row.isEditing ? (
                        <input className="table-input" type="number" value={row.marza_at_sale} onChange={(e) => setRowProp(row.id, "marza_at_sale", e.target.value)} />
                      ) : row.marza_at_sale}
                    </td>
                    :<></>
                    }
                    <td>{row.vat_nazwa_at_sale}</td>
                    {canSeeCostsMargin
                    ?<td className="text-right">{row.razem_netto}</td>
                    :<></>
                    }

                    {canSeeCostsMargin
                    ?<td className="text-right">{row.razem_brutto}</td>
                    :<></>
                    }
                    <td className="text-center">
                      <div className="action-row">
                        <button type="button" onClick={() => handleRemoveRow(row.id)} className="small-button">❌</button>
                        {row.isEditing ? (
                          <button type="button" onClick={() => handleSaveRow(row.id)} className="small-button">💾</button>
                        ) : (
                          <button type="button" onClick={() => handleEditRow(row.id)} className="small-button" disabled={otherRowEditing(row.id)}>✏️</button>
                        )}
                      </div>
                    </td>
                    <td>
                      <input type="checkbox" disabled={!row.isEditing} checked={!!row.in_sprzedaz} onChange={(e) => setRowProp(row.id, "in_sprzedaz", e.target.checked)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* WERSJA MOBILE */}
        <div className="mobile-view">
          {rows.map((row, idx) => (
            <div key={row.id} className={`mobile-row-card ${row.isEditing ? 'editing' : ''}`}>
              <div className="card-mobile-header">
                <span className="lp-badge">#{idx + 1}</span>
                <div className="mobile-actions">
                  <button type="button" className="m-btn-delete" onClick={() => handleRemoveRow(row.id)}>Usuń</button>
                  {row.isEditing ? (
                    <button type="button" className="m-btn-save" onClick={() => handleSaveRow(row.id)}>Zapisz</button>
                  ) : (
                    <button type="button" className="m-btn-edit" onClick={() => handleEditRow(row.id)} disabled={otherRowEditing(row.id)}>Edytuj</button>
                  )}
                </div>
              </div>

              <div className="card-mobile-body">
                <div className="m-field">
                  <label>Towar / Usługa:</label>
                  {row.isEditing ? (
                    <AutocompleteInput
                      value={row.nazwa_at_sale}
                      towary={towary}
                      onChange={(val) => handleNameChange(row.id, val)}
                      onSelect={(item) => handleTowarSelect(row.id, item)}
                    />
                  ) : <div className="m-val-important">{row.nazwa_at_sale}</div>}
                </div>

                { canSeeCostsMargin
                ?<div className="m-grid-2">
                  <div className="m-field">
                    <label>Ilość:</label>
                    {row.isEditing ? (
                      <input type="number" value={row.ilosc} onChange={(e) => handleQtyChange(row.id, e.target.value)} />
                    ) : <span>{row.ilosc} {row.jednostka_symbol_at_sale}</span>}
                  </div>
                  
                  <div className="m-field">
                    <label>Koszt netto:</label>
                    {row.isEditing ? (
                      <input type="number" value={row.koszt_jednostkowy_netto_at_sale} onChange={(e) => setRowProp(row.id, "koszt_jednostkowy_netto_at_sale", e.target.value)} />
                    ) : <span>{row.koszt_jednostkowy_netto_at_sale} zł</span>}
                  </div>
                  
                </div>
                :<div className="m-grid-2">
                  <div className="m-field">
                    <label>Ilość:</label>
                    {row.isEditing ? (
                      <input type="number" value={row.ilosc} onChange={(e) => handleQtyChange(row.id, e.target.value)} />
                    ) : <span>{row.ilosc} {row.jednostka_symbol_at_sale}</span>}
                  </div>
                  
                  <div className="m-field">
                    <label>Stawka VAT:</label>
                    {row.isEditing ? (
                      <select value={String(row.vat_id ?? "")} onChange={(e) => handleVatChange(row.id, e.target.value)}>
                        {slowniki.stawkiVat?.map((v) => <option key={v.id} value={String(v.id)}>{v.nazwa}</option>)}
                      </select>
                    ) : <span>{row.vat_nazwa_at_sale}</span>}
                  </div>
                  
                </div>
                }
                
                { canSeeCostsMargin
                ?<div className="m-grid-2">
                   <div className="m-field">
                    <label>Marża %:</label>
                    {row.isEditing ? (
                      <input type="number" value={row.marza_at_sale} onChange={(e) => setRowProp(row.id, "marza_at_sale", e.target.value)} />
                    ) : <span>{row.marza_at_sale}%</span>}
                  </div>
                  <div className="m-field">
                    <label>Stawka VAT:</label>
                    {row.isEditing ? (
                      <select value={String(row.vat_id ?? "")} onChange={(e) => handleVatChange(row.id, e.target.value)}>
                        {slowniki.stawkiVat?.map((v) => <option key={v.id} value={String(v.id)}>{v.nazwa}</option>)}
                      </select>
                    ) : <span>{row.vat_nazwa_at_sale}</span>}
                  </div>
                </div>
                :<></>
                }

                <div className="m-footer-sums">
                  
                  {canSeeCostsMargin
                  ?<><div className="m-sum-item">Netto: <strong>{row.razem_netto} zł</strong></div>
                  <div className="m-sum-item">Brutto: <strong>{row.razem_brutto} zł</strong></div></>
                  :<></>
                  }
                  <label className="m-checkbox-wrap">
                    <input type="checkbox" disabled={!row.isEditing} checked={!!row.in_sprzedaz} onChange={(e) => setRowProp(row.id, "in_sprzedaz", e.target.checked)} />
                    <span>Ujęte w sprzedaży</span>
                  </label>
                </div>
              </div>
            </div>
          ))}
        </div>
        {canSeeCostsMargin
        ?<div className="section-sum">
          Suma kosztów: Netto: {round2(sumsKoszt.netto)} zł | Brutto: {round2(sumsKoszt.brutto)} zł
        </div>
        :<></>
        }
      </div>

      {/* ======== SPRZEDAŻ ======== */}
      {canSeeSalePrices ? (
      <div className="kst-section mt-20">
        <h4>Sprzedaż (dla klienta)</h4>
        
        <div className="desktop-view">
          <div className="kst-card">
            <table className="costs-table">
              <thead>
                <tr>
                  <th>L.P.</th>
                  <th>NAZWA</th>
                  <th>ILOŚĆ</th>
                  <th>CENA NETTO</th>
                  <th>BRUTTO JEDN.</th>
                  <th>VAT</th>
                  <th>WARTOŚĆ NETTO</th>
                  <th>WARTOŚĆ BRUTTO</th>
                  <th>AKCJE</th>
                </tr>
              </thead>
              <tbody>
                {rows.filter(r => !!r.in_sprzedaz).map((row, idx) => (
                  <tr key={`sprz-${row.id}`}>
                    <td>{idx + 1}</td>
                    <td>{row.nazwa_at_sale}</td>
                    <td>{row.ilosc} {row.jednostka_symbol_at_sale}</td>
                    <td>
                      {row.isEditingSales ? (
                        <input className="table-input" type="number" value={row.cena_netto_at_sale} onChange={(e) => handleSalesPriceChange(row.id, e.target.value)} />
                      ) : row.cena_netto_at_sale}
                    </td>
                    <td>{row.sprz_cena_brutto_unit}</td>
                    <td>{row.vat_proc_at_sale}%</td>
                    <td className="text-right">{row.sprz_razem_netto}</td>
                    <td className="text-right">{row.sprz_razem_brutto}</td>
                    <td className="text-center">
                      {row.isEditingSales ? (
                        <button type="button" onClick={() => handleSalesSaveRow(row.id)} className="small-button">💾</button>
                      ) : (
                        <button type="button" onClick={() => handleSalesEditRow(row.id)} className="small-button" disabled={otherRowEditing(row.id) || row.isEditing}>✏️</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mobile-view">
          {rows.filter(r => !!r.in_sprzedaz).map((row, idx) => (
            <div key={`sprz-mob-${row.id}`} className={`mobile-row-card sales-card ${row.isEditingSales ? 'editing' : ''}`}>
               <div className="card-mobile-header">
                <span className="lp-badge">#{idx+1}</span>
                <div className="mobile-actions">
                  {row.isEditingSales ? (
                    <button type="button" className="m-btn-save" onClick={() => handleSalesSaveRow(row.id)}>Zapisz</button>
                  ) : (
                    <button type="button" className="m-btn-edit-price" onClick={() => handleSalesEditRow(row.id)} disabled={otherRowEditing(row.id) || row.isEditing}>Zmień cenę</button>
                  )}
                </div>
              </div>
              <div className="card-mobile-body">
                <div className="m-val-important">{row.nazwa_at_sale}</div>
                <p className="m-info">Ilość: {row.ilosc} {row.jednostka_symbol_at_sale} | VAT: {row.vat_proc_at_sale}%</p>
                
                <div className="m-grid-2">
                  <div className="m-field">
                    <label>Cena netto jedn.:</label>
                    {row.isEditingSales ? (
                      <input type="number" value={row.cena_netto_at_sale} onChange={(e) => handleSalesPriceChange(row.id, e.target.value)} />
                    ) : <span>{row.cena_netto_at_sale} zł</span>}
                  </div>
                  <div className="m-field">
                    <label>Brutto jedn.:</label>
                    <span>{row.sprz_cena_brutto_unit} zł</span>
                  </div>
                </div>
                <div className="m-footer-sums">
                  <div className="m-sum-item">Wartość netto: <strong>{row.sprz_razem_netto} zł</strong></div>
                  <div className="m-sum-item">Wartość brutto: <strong>{row.sprz_razem_brutto} zł</strong></div>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="section-sum">
          Suma sprzedaży: Netto: {round2(sumsSprzedaz.netto)} zł | Brutto: {round2(sumsSprzedaz.brutto)} zł
        </div>
      </div>
      ) : null}
    </div>
  );
}